import { zodResolver } from "@hookform/resolvers/zod";
import type {
  TaskDefinitionFormOverrideSchema,
  TaskDefinitionFormSchema,
} from "api";
import type { FormEvent } from "react";
import { useCallback, useState } from "react";
import type { DefaultValues, UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import type { ZodSchema } from "zod";

export interface UseForkFormProps<
  TFieldValues extends
    | TaskDefinitionFormSchema
    | TaskDefinitionFormOverrideSchema,
> {
  schema: ZodSchema<TFieldValues, any, any>;
  defaultValues: DefaultValues<TFieldValues>;
  onSubmit: (data: TFieldValues) => Promise<void>;
}

export interface UseTaskDefinitionFormReturn<
  TFieldValues extends
    | TaskDefinitionFormSchema
    | TaskDefinitionFormOverrideSchema,
> {
  form: UseFormReturn<TFieldValues>;
  error: string;
  submit: (e: FormEvent) => void;
}

export function useTaskDefinitionForm<
  TFieldValues extends
    | TaskDefinitionFormSchema
    | TaskDefinitionFormOverrideSchema,
>(
  props: UseForkFormProps<TFieldValues>,
): UseTaskDefinitionFormReturn<TFieldValues> {
  const { defaultValues, schema, onSubmit } = props;
  const [error, setError] = useState("");

  const form = useForm<TFieldValues>({
    resolver: zodResolver(schema),
    defaultValues: { ...defaultValues },
  });

  const submit = useCallback(
    (e: FormEvent) => {
      setError("");

      form
        .handleSubmit(async (data: TFieldValues) => {
          try {
            await onSubmit(data);
          } catch (err) {
            setError(`${err}`);
            e.preventDefault();
          }
        })(e)
        .catch(err => setError(`${err}`));
    },
    [form, onSubmit],
  );

  return {
    form,
    error,
    submit,
  };
}
